import React, { useState, useEffect, useRef } from "react";
import {
  Box, VStack, Heading, Text, useColorModeValue, Button,
  SimpleGrid, HStack, useBreakpointValue, useToast, Tooltip, Spinner, Badge, Icon, Divider,
} from "@chakra-ui/react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { StudentInfo, ExamAttemptListing, ExamAttemptDetails } from "../Common/types";

import { useLocation } from "react-router-dom";

import {
  FiBookOpen, FiClock, FiCalendar, FiCheckCircle, FiLock, FiPlayCircle, FiAward
} from "react-icons/fi";
import QuestionSetViewAttempt from "../Components/QuestionSetViewAttempt";
import { toUtcISOString, parseUtc, fmtLocal } from "../../utils/dates";
import { useTenant } from "../Routers/TenantContextProvider";

const StudentExamAttempts = () => {
  const [currentStudent, setCurrentStudent] = useState<StudentInfo>();
  const [examAttemptListings, setExamAttemptListings] = useState<ExamAttemptListing[]>([]);
 
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const toast = useToast();

     const { tenant, loading, error } = useTenant();
  

  const cardBg = useColorModeValue("blue.50", "blue.700");

  const textColor = useColorModeValue("blue.800", "blue.50");
  const bannerColor = useColorModeValue("yellow.100", "yellow.700");
  const showBanner = useBreakpointValue({ base: false, md: true });

  const token = localStorage.getItem("token");
  const isLoggedIn = Boolean(token);

  const location = useLocation();



  const fetchCurrentStudent = async () => {
    try {
      const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";
      const response = await axios.get(`${apiurl}/students-info`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setCurrentStudent(response.data);
    } catch (error) {
      console.error("Error fetching Student Info:", error);
    }
  };

  const fetchExamAttemptListings = async () => {
    setIsLoading(true);
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
    try {
      const response = await axios.get(`${apiUrl}/student/exam-attempt-listings/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setExamAttemptListings(response.data);
    } catch (error) {
      console.error("Failed to load exam attempt listings", error);
    }
    setIsLoading(false);
  };


  const handleAttemptDetails = async (exam_attempt_id: number) => {

  //    navigate(`/coaching/dashboard/student-exam-attempts/score-view-attempt/${exam_attempt_id}`);

  if (tenant)
    {
      navigate(`/platform/${tenant.tenant_slug}/app/student-exam-attempts/score-view-attempt/${exam_attempt_id}`);
    }

  }


    useEffect(() => {
      fetchCurrentStudent();
    }, []);
  
    useEffect(() => {
      if (currentStudent) {
        fetchExamAttemptListings();
      }
    }, [currentStudent]);


    
      const getStatusProps = (status: string) => {
        switch (status) {
          case "COMPLETED":
            return { colorScheme: "green", icon: FiCheckCircle, label: "Open for entry" };
          case "IN_PROGRESS":
            return { colorScheme: "red", icon: FiPlayCircle, label: "Exam in progress" };
          case "DISQUALIFIED":
          default:
            return { colorScheme: "gray", icon: FiLock, label: "Disqualified" };
        }
      };

      const [selectedIndex, setSelectedIndex] = useState(0);

 
return (
  <Box>
    {/* Banner */}
    {showBanner ? (
      <Box bgColor={bannerColor} p={4} height="70px">
        <HStack>
          <Heading size="sm">Hello {currentStudent?.givenName}!</Heading>
          <Text fontSize="md">Checkout your exam results.</Text>
        </HStack>
      </Box>
    ) : (
      <Box p={4} bgColor={bannerColor} height="50px">
        <VStack>
          <Text fontWeight="semibold" fontSize="md">
            My Exam Results
          </Text>
        </VStack>
      </Box>
    )}

<Box as="main" mx="auto" maxWidth="1200px" p={4} color={textColor}>
{/*   <HStack mb={4} justifyContent="space-between">
    <Heading size="md">Exam Results</Heading>
  </HStack> */}

  {isLoading ? (
    <Spinner size="xl" />
  ) : (
    <SimpleGrid columns={{ base: 1, sm: 1, md: 3 }} spacing={6} mt={4}>
      {examAttemptListings.map((examAttemptListing) => {
        const statusProps = getStatusProps(examAttemptListing.status);

        return (
          <Box
            key={examAttemptListing.exam_attempt_id}
            p={6}
            bg={cardBg}
            borderRadius="lg"
            boxShadow="xl"
            borderWidth="1px"
            borderColor="gray.200"
            transition="0.3s"
            _hover={{ transform: "translateY(-4px)", boxShadow: "2xl" }}
          >
            {/* Title */}
            <Heading size="md" mb={2} noOfLines={2}>
              {examAttemptListing.title}
            </Heading>

            {/* Info */}
            <VStack align="start" spacing={2} fontSize="sm" mb={3}>
              <HStack spacing={2}>
                <Icon as={FiCalendar} color="blue.500" />
                <Text>
                  <strong>Date:</strong>{" "}
                  {examAttemptListing.attempted_on
                    ? fmtLocal(examAttemptListing.attempted_on, {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                        weekday: undefined,
                        second: undefined,
                        timeZoneName: undefined,
                      })?.replace(",", " at")
                    : "Not attempted"}
                </Text>
              </HStack>

              <HStack spacing={2}>
                <Icon as={FiClock} color="orange.500" />
                <Text>
                  <strong>Duration:</strong>{" "}
                  {examAttemptListing.duration_minutes} mins
                </Text>
              </HStack>

              {examAttemptListing.score_percentage !== undefined && (
                <HStack spacing={2}>
                  <Icon as={FiAward} color="yellow.500" />
                  <Text>
                    <strong>Score:</strong>{" "}
                    {examAttemptListing.score_percentage?.toFixed(2)}%
                  </Text>
                </HStack>
              )}
            </VStack>

            {/* Status */}
            <Tooltip label={statusProps.label} placement="top">
              <Badge
                colorScheme={statusProps.colorScheme}
                fontSize="0.75em"
                px={3}
                py={1}
                borderRadius="full"
                mb={3}
              >
                <HStack spacing={1}>
                  <Icon as={statusProps.icon} boxSize={3.5} />
                  <Text>{examAttemptListing.status}</Text>
                </HStack>
              </Badge>
            </Tooltip>

            {/* Action */}
            <Button
              colorScheme="purple"
              size="sm"
              width="full"
              onClick={() =>
                handleAttemptDetails(examAttemptListing.exam_attempt_id)
              }
            >
              View Result Details
            </Button>
          </Box>
        );
      })}
    </SimpleGrid>
  )}
</Box>
  </Box>
);

};

export default StudentExamAttempts;
